import { Controller } from "@hotwired/stimulus"
import { loadScript } from "@paypal/paypal-js"

import { buttonStyle, cardStyle } from "../helpers/paypal"
import { jsonFetch } from "../helpers/json_fetch"
import { debug } from "../helpers/debug"

export default class extends Controller {
  static values = {
    // Options
    showButton: Boolean,
    showCard: Boolean,

    // PayPal SDK parameters
    clientId: String,
    locale: String,
    currency: String,
    expirationPlaceholder: String,

    // Fetch URLs
    tokensUrl: String,
    cardIntentsUrl: String,
    buttonIntentsUrl: String,
    capturesUrl: String,
  }

  static targets = [
    "buttonContainer",
    "cardForm",
    "cardHolderName",

    // Errors
    "error",
    "buttonLoadError",
    "unprocessableCardError",

    // Loaders
    "buttonLoading",
    "cardLoading",
    "paymentLoading",
  ]

  // Initialize the PayPal SDK & show Button
  connect() {
    this
      .#setupCard()
      .then(this.#insertScript)
      .then(this.#initButton)
      .catch(e => this.#error(`PayPal connect error: ${e}`, e))
      .then(() => {
        if (this.hasCardLoadingTarget) this.cardLoadingTarget.hidden = true
        if (this.hasButtonLoadingTarget) this.buttonLoadingTarget.hidden = true
        this.element.classList.remove("is-loading")
      })
  }

  // Show the PayPal Card
  mountCard = () => {
    if (!this.cardFormTarget.hidden) return

    this.cardLoadingTarget.hidden = false

    this
      .#initCard()
      .then(() => this.cardFormTarget.hidden = false)
      .catch(e => this.#error(this.#cardLoadErrorMessage(e), e))
      .then(() => this.cardLoadingTarget.hidden = true)
  }

  // Trigger a card payment
  payCard = (e) => {
    e.preventDefault()

    this.cardFormTarget.classList.add("is-loading")
    this.paymentLoadingTarget.hidden = false
    this.errorTarget.innerHTML = ""
    this.unprocessableCardErrorTarget.hidden = true

    try {
      this
        .hostedFields
        .submit({
          cardholderName: this.cardHolderNameTarget.value,
          contingencies: ["SCA_WHEN_REQUIRED"],
        })
        .then(this.#captureCard)
        .then(() => {
          this.cardFormTarget.classList.remove("is-loading")
          this.paymentLoadingTarget.hidden = true
        })
    } catch (e) {
      this.#error(`Hosted card load error ${e.name}`, e)
      this.cardFormTarget.classList.remove("is-loading")
      this.paymentLoadingTarget.hidden = true
    }
  }

  // Create a client token that is needed to insert the script
  #setupCard = () => {
    if (!this.showCardValue) return Promise.resolve()

    const body = { payment_method: "paypal_card" }

    return jsonFetch(this.tokensUrlValue, { method: "POST", body })
      .then(json => this.clientToken = json.client_token)
      .catch(e => Promise.reject(`Token fetch error ${e.statusText}`))
  }

  // Insert PayPal’s script
  #insertScript = () => {
    const components = []
    if (this.showButtonValue) components.push("buttons")
    if (this.showCardValue) components.push("hosted-fields")

    return loadScript({
      "client-id": this.clientIdValue,
      locale: this.localeValue,
      currency: this.currencyValue,
      components: components.join(","),
      "data-client-token": this.clientToken,
    })
  }

  // https://developer.paypal.com/sdk/js/reference/
  #initButton = () => {
    if (!this.showButtonValue) return Promise.resolve()

    if (!paypal.Buttons) {
      this.buttonLoadErrorTarget.hidden = false
      return Promise.resolve()
    }

    try {
      return paypal
        .Buttons({
          fundingSource: paypal.FUNDING.PAYPAL,
          style: buttonStyle,
          createOrder: this.#createButtonOrder,
          onApprove: this.#captureButton,
          onError: this.#buttonError,
        })
        .render(this.buttonContainerTarget)
    } catch (e) {
      return Promise.reject(`Button initialization error: ${e}`)
    }
  }

  #initCard = () => {
    if (!paypal.HostedFields.isEligible()) {
      return Promise.reject("Hosted card fields not eligible")
    }

    return paypal
      .HostedFields
      .render({
        createOrder: this.#createCardOrder,
        styles: cardStyle(),
        fields: {
          number: {
            selector: "#paypal-card-number",
            placeholder: "•••• •••• •••• ••••",
          },
          cvv: {
            selector: "#paypal-cvc",
            placeholder: "123",
          },
          expirationDate: {
            selector: "#paypal-expiration-date",
            placeholder: this.expirationPlaceholderValue,
          },
        },
      })
      .then(fields => this.hostedFields = fields)
  }

  // Create a PayPal payment intent for a button payment.
  // https://developer.paypal.com/docs/business/checkout/server-side-api-calls/create-order/
  #createButtonOrder = (_data, _actions) => {
    // This helps close the payment accordion
    this.buttonContainerTarget.dispatchEvent(new MouseEvent("click"))

    this.errorTarget.innerHTML = ""

    return jsonFetch(this.buttonIntentsUrlValue, { method: "POST" })
      .then(json => json.paypal_order.code)
      .catch(e => this.#error(this.#intentsErrorMessage(e), e))
  }

  // Create a PayPal payment intent for a card payment and return a promis with
  // the paypal order id.
  #createCardOrder = (_data, _actions) => {
    return jsonFetch(this.cardIntentsUrlValue, { method: "POST" })
      .then(json => json.paypal_order.code)
      .catch(e => this.#error(`Card payment intent error: ${e.statusText}`, e))
  }

  // Capture the button payment intent.
  // https://developer.paypal.com/docs/business/checkout/server-side-api-calls/capture-order/
  #captureButton = (data, _actions) => {
    return this.#capture(data.orderID)
      .catch(e => {
        this.#error(`PayPal error: ${this.#captureErrorMessage(e)}`, e)
      })
  }

  // Capture the card payment intent.
  #captureCard = (data, _actions) => {
    return this.#capture(data.orderId)
      .catch(e => {
        if (this.#isCardError(e)) {
          this.unprocessableCardErrorTarget.hidden = false
        } else {
          this.#error(`Card error: ${this.#captureErrorMessage(e)}`, e)
        }
      })
  }

  #isCardError = (e) => {
    if (e.name == "UNPROCESSABLE_ENTITY") return true

    return (
      e.name == "VALIDATION#ERROR" &&
      e?.details?.[0]?.issue == "Invalid card number"
    )
  }

  #cardLoadErrorMessage = (e) => {
    if (e.code == "HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME") {
      return "Hosted card fields already loaded, please refresh the page"
    } else if (e.code == "HOSTED_FIELDS_TIMEOUT") {
      return "Hosted card fields took too long to load, please refresh the " +
             "page to try again"
    } else {
      return `Hosted card load error: ${JSON.stringify(e)}`
    }
  }

  #intentsErrorMessage = (e) => {
    if (e.status === 429) {
      return "Too many requests, please wait before trying again."
    }

    const message = e.statusText || e.responseText || JSON.stringify(e)
    return `Payment intent error: ${message}`
  }

  #captureErrorMessage(e) {
    if (e.name == "INVALID_REQUEST" && e.details) {
      return e.details.map(detail => {
        const name = detail.field.replace("/payment_source/card/", "")
        return `${name}: ${detail.description}`
      }).join(",\n")
    }

    if (e.message) {
      let message = e.message || ""
      if (e.details?.[0]?.description) {
        message += ` (${e.details[0].description})`
      }
      return message
    }

    return e.responseJSON?.error || e.responseText || "Unknown error"
  }

  #capture(id) {
    const body = { paypal_order_code: id }

    return jsonFetch(this.capturesUrlValue, { method: "POST", body })
      .then(data => {
        const url = data.orderPath || data.redirectUrl
        if (!url) throw data.error || data.state

        // This clears the cart
        window.location = url
      })
  }

  #buttonError = (error) => {
    if (error == "Detected popup close") return

    this.#error(`PayPal error: ${error.message}`, error)
  }

  #error = (message, error) => {
    console.error("PayPal error", message, error)
    this.errorTarget.innerText = message
    debug(message, error)
  }
}
